import { useState } from "react";

import { Server } from "@wajeez/wajeez.microservices";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import ContentCarousel from "@components/items/display/ContentCarousel";
import BookCard from "@components/items/display/BookCard";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";

import { useLanguage } from "@modules/localizations/LanguageContext";
import { ExtraProps } from "@modules/content/components";
import { OnItemPress } from "@modules/content/hooks/useComponentData";

export interface RepeatedPrayerTypesGridParameters {
  title: string;
}

export interface RepeatedPrayerTypesGridItemParameters {
  title: string | null;
}

type ItemParams = {
  id: string | undefined;
} & {
  [K in keyof RepeatedPrayerTypesGridItemParameters]?:
    | RepeatedPrayerTypesGridItemParameters[K]
    | undefined;
};

interface RepeatedPrayerTypesGridProps {
  onItemPress: OnItemPress<ItemParams>;
  contents:
    | (Server.ContentEntityOutput & {
        isLocked: boolean | undefined;
        lockType: Server.LockType | undefined;
      })[]
    | undefined;
  isLoading: boolean;
  parameterValues: RepeatedPrayerTypesGridParameters;
  items: ItemParams[];
  extraProps?: ExtraProps;
}

export const RepeatedPrayerTypesGrid = ({
  onItemPress,
  contents,
  isLoading,
  parameterValues,
  items,
  extraProps,
}: RepeatedPrayerTypesGridProps) => {
  const navigate = useAppNavigate();

  const [seeAllShown, setSeeAllShown] = useState(false);

  const { direction } = useLanguage();

  const { title } = parameterValues;

  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  return (
    <div
      className={`relative w-full ${
        (isLoadingVisible || isLoading) && "h-[43px]"
      }`}
    >
      <div
        className={`absolute top-0 h-full w-full px-10 ltr:left-0 rtl:right-0 ${
          (isLoadingVisible || isLoading) && "z-10 bg-white dark:bg-black"
        }`}
      >
        <RepeatedPrayerTypesGridLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="flex w-full flex-col items-center justify-center gap-y-6 px-10 pb-[15px]">
          <div className="flex w-full items-center justify-between">
            <div
              className={`text-[20px] font-bold ${
                extraProps?.titleClassName
                  ? extraProps.titleClassName
                  : "dark:text-dark-ramadan-primary"
              } text-[44px] font-bold text-ramadan-primary dark:text-dark-ramadan-primary`}
            >
              {title}
            </div>
          </div>
          <div className="mt-[40px] flex w-full items-center justify-start text-ramadan-primary dark:text-dark-ramadan-primary">
            <ContentCarousel
              className="w-[95%] max-w-full"
              itemClassName="basis-[1/8]"
              options={{
                direction,
              }}
              items={items.map((item, i) => {
                const content = contents.find((c) => c.id === item.id);

                return (
                  <div key={item.id} className="pb-2">
                    <div
                      className={`flex h-[43px] min-w-[112px] cursor-pointer items-center justify-center rounded-[8px] border border-[#898989] p-4 text-[15px] font-medium ${
                        extraProps?.selectedContentId === content?.id &&
                        "bg-ramadan-primary text-dark-ramadan-primary dark:bg-dark-ramadan-primary dark:text-ramadan-primary"
                      }`}
                      onClick={() => {
                        content &&
                          onItemPress(content, item, {
                            isLocked: content.isLocked,
                          });
                      }}
                    >
                      {item.title}
                    </div>
                  </div>
                );
              })}
              navigationStyle="top-1/2 -translate-y-1/2"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RepeatedPrayerTypesGridLoading = ({
  isLoading,
}: {
  isLoading: boolean;
}) => {
  return isLoading ? null : null;
};
